import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CheckCircle2, Circle, Users } from 'lucide-react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { loadUserProgress } from '../utils/progress';
import problemsData from '../data/problems.json';

const PREVIEW_COUNT = 4;

const DIFFICULTY_OPTIONS = [
  { value: 'all', label: 'All Difficulties' },
  { value: 'easy', label: 'Beginner' },
  { value: 'medium', label: 'Intermediate' },
  { value: 'hard', label: 'Hard' },
];

const CATEGORY_OPTIONS = [
  {
    value: 'fundamentals',
    label: 'Fundamentals',
    categories: [
      'Observables',
      'Quantum Fundamentals',
      'Quantum Measurement',
      'State Preparation',
      'Superposition',
    ],
  },
  {
    value: 'gates-circuits',
    label: 'Gates & Circuits',
    categories: ['Gate Decomposition', 'Quantum Gates', 'Reversible Computing'],
  },
  {
    value: 'entanglement-communication',
    label: 'Entanglement & Communication',
    categories: ['Entanglement', 'Quantum Communication', 'Quantum Entanglement'],
  },
  {
    value: 'algorithms',
    label: 'Quantum Algorithms',
    categories: [
      'Bernstein-Vazirani',
      'Deutsch Algorithm',
      'Deutsch-Jozsa Algorithm',
      "Grover's Algorithm",
      'Oracles',
      'Oracles / Grover',
      'Quantum Fourier Transform',
      'Quantum Phase Estimation',
      "Simon's Algorithm",
    ],
  },
  {
    value: 'noise-error-correction',
    label: 'Noise & Error Correction',
    categories: ['Noise Simulation', 'Quantum Error Correction'],
  },
  {
    value: 'variational-ml',
    label: 'Variational & Machine Learning',
    categories: [
      'Quantum Machine Learning',
      'Variational Algorithms',
      'Variational Circuits',
      'Variational Quantum Algorithms',
    ],
  },
];

const PROBLEM_FOLDERS = [
  { key: 'beginner', label: 'Beginner', difficulty: 'easy' },
  { key: 'intermediate', label: 'Intermediate', difficulty: 'medium' },
  { key: 'hard', label: 'Hard', difficulty: 'hard' },
];

const Problems = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [showAllInFolder, setShowAllInFolder] = useState(() => new Set());
  const [user, setUser] = useState(null);
  const [completedProblems, setCompletedProblems] = useState({});

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        const { completedProblems } = await loadUserProgress(user.uid);
        setCompletedProblems(completedProblems);
      } else {
        setCompletedProblems({});
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleStorageChange = async (e) => {
      if (e.key === 'userProgress' && user) {
        const { completedProblems } = await loadUserProgress(user.uid);
        setCompletedProblems(completedProblems);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    const handleProgressUpdate = async () => {
      if (user) {
        const { completedProblems } = await loadUserProgress(user.uid);
        setCompletedProblems(completedProblems);
      }
    };

    window.addEventListener('progressUpdated', handleProgressUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('progressUpdated', handleProgressUpdate);
    };
  }, [user]);

  useEffect(() => {
    setShowAllInFolder(new Set());
  }, [searchTerm, selectedCategory, selectedDifficulty]);

  const problems = Object.keys(problemsData).map(id => {
    const problem = problemsData[id];
    const descriptionHtml = problem.description || '';
    const match = descriptionHtml.match(/<p>(.*?)<\/p>/);
    const shortDescription = match ? match[1].replace(/<[^>]+>/g, '') : 'No description available.';

    return {
      id,
      ...problem,
      description: shortDescription.substring(0, 100) + (shortDescription.length > 100 ? '...' : ''),
      completed: completedProblems[id] ? true : false,
    };
  });

  const selectedCategoryOption = CATEGORY_OPTIONS.find(
    ({ value }) => value === selectedCategory
  );

  const filteredProblems = problems.filter(problem => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !term ||
      problem.title.toLowerCase().includes(term) ||
      problem.category.toLowerCase().includes(term);
    const matchesCategory = selectedCategory === 'all' ||
      selectedCategoryOption?.categories.includes(problem.category);
    const matchesDifficulty = selectedDifficulty === 'all' ||
      problem.difficulty === selectedDifficulty;
    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedDifficulty('all');
  };

  const handleProblemClick = (problemId) => {
    navigate(`/problems/${problemId}`);
  };

  const showAllForFolder = (folderKey) => {
    setShowAllInFolder(prev => new Set(prev).add(folderKey));
  };

  const renderProblemRow = (problem) => (
    <div
      key={problem.id}
      role="button"
      tabIndex={0}
      className="problem-row"
      onClick={() => handleProblemClick(problem.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleProblemClick(problem.id);
        }
      }}
    >
      <span className="problem-row__status">
        {problem.completed
          ? <CheckCircle2 size={16} color="#7a9a9a" />
          : <Circle size={16} color="#c5c5c7" />
        }
      </span>
      <span className="problem-row__num">#{problem.id}</span>
      <span className="problem-row__title">{problem.title}</span>
      <span className="problem-row__category">{problem.category}</span>
      <span className="problem-row__submissions">
        <Users size={12} color="#b0b0b8" />
        {problem.submissions}
      </span>
    </div>
  );

  return (
    <div className="problems-page">
      <div className="card problems-filters-card">
        <div className="problems-toolbar">
          <div className="problems-search">
            <Search size={20} color="#6e6e73" aria-hidden />
            <input
              type="text"
              className="problems-search-input"
              placeholder="Search problems..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="problems-filter-group">
            <select
              className="problems-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              aria-label="Filter by topic"
            >
              <option value="all">All Topics</option>
              {CATEGORY_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <select
              className="problems-select"
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
              aria-label="Filter by difficulty"
            >
              {DIFFICULTY_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {filteredProblems.length > 0 ? (
        <div className="problems-folders">
          {PROBLEM_FOLDERS.map((folder) => {
            const folderProblems = filteredProblems.filter(
              (problem) => problem.difficulty === folder.difficulty
            );
            const doneCount = folderProblems.filter(p => p.completed).length;
            const showAll = showAllInFolder.has(folder.key);
            const visibleProblems = showAll
              ? folderProblems
              : folderProblems.slice(0, PREVIEW_COUNT);
            const hiddenCount = showAll
              ? 0
              : Math.max(0, folderProblems.length - PREVIEW_COUNT);

            return (
              <section
                key={folder.key}
                className={`problems-folder problems-folder--${folder.difficulty}`}
              >
                <div className="problems-folder__header">
                  <span className="problems-folder__label">{folder.label}</span>
                  <span className="problems-folder__progress">
                    {doneCount}/{folderProblems.length}
                  </span>
                </div>

                {folderProblems.length > 0 ? (
                  <>
                    <div className="problems-folder__list" role="list">
                      {visibleProblems.map((problem) => renderProblemRow(problem))}
                    </div>
                    {hiddenCount > 0 && (
                      <button
                        type="button"
                        className="problems-folder__more"
                        onClick={() => showAllForFolder(folder.key)}
                      >
                        Show {hiddenCount} more
                      </button>
                    )}
                  </>
                ) : (
                  <p className="problems-folder__empty">No problems in this category yet.</p>
                )}
              </section>
            );
          })}
        </div>
      ) : (
        <div className="card problems-empty-card">
          <div className="problems-empty-message">
            No problems found matching your criteria
          </div>
          <button
            type="button"
            className="problems-empty-clear"
            onClick={clearFilters}
          >
            Clear Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default Problems;
